import { useRef, type ReactNode } from "react";
import {
  Badge,
  Building2,
  ClipboardList,
  DollarSign,
  FileSignature,
  ImageIcon,
  Loader2,
  Minus,
  PenSquare,
  SquareDashedBottom,
  Type,
  Upload,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { TemplatePageElementType } from "../../types";
import type { BuilderElementDraft } from "./BuilderSectionElementList";
import { useBuilderImageUpload } from "../../hooks/useBuilderImageUpload";

interface BuilderElementContentEditorProps {
  element: BuilderElementDraft;
  readOnly?: boolean;
  onChange: (updates: Partial<BuilderElementDraft>) => void;
  className?: string;
}

function getStringContent(
  element: BuilderElementDraft,
  key: string,
  fallback = ""
) {
  const value = element.content?.[key];
  return typeof value === "string" ? value : fallback;
}

function getNumberContent(
  element: BuilderElementDraft,
  key: string,
  fallback: number
) {
  const value = element.content?.[key];
  return typeof value === "number" ? value : fallback;
}

function getBooleanContent(
  element: BuilderElementDraft,
  key: string,
  fallback: boolean
) {
  const value = element.content?.[key];
  return typeof value === "boolean" ? value : fallback;
}

function updateElementContent(
  element: BuilderElementDraft,
  key: string,
  value: string | number | boolean | null
) {
  return {
    content: {
      ...(element.content ?? {}),
      [key]: value,
    },
  };
}

function elementTypeLabel(type: TemplatePageElementType) {
  switch (type) {
    case "text":
      return "Text";
    case "rich_text":
      return "Rich Text";
    case "image":
      return "Image";
    case "logo":
      return "Logo";
    case "divider":
      return "Divider";
    case "spacer":
      return "Spacer";
    case "service_table":
      return "Service Table";
    case "pricing_summary":
      return "Pricing Summary";
    case "signature_block":
      return "Signature Block";
    case "badge":
      return "Badge";
    case "company_info":
      return "Company Info";
    case "project_summary":
      return "Project Summary";
    case "static_content":
      return "Static Content";
    case "custom":
      return "Custom";
    default:
      return "Element";
  }
}

function elementTypeIcon(type: TemplatePageElementType): ReactNode {
  switch (type) {
    case "text":
      return <Type className="h-4 w-4" />;
    case "rich_text":
      return <PenSquare className="h-4 w-4" />;
    case "image":
    case "logo":
      return <ImageIcon className="h-4 w-4" />;
    case "divider":
      return <Minus className="h-4 w-4" />;
    case "spacer":
      return <SquareDashedBottom className="h-4 w-4" />;
    case "badge":
      return <Badge className="h-4 w-4" />;
    case "company_info":
      return <Building2 className="h-4 w-4" />;
    case "project_summary":
    case "service_table":
      return <ClipboardList className="h-4 w-4" />;
    case "pricing_summary":
      return <DollarSign className="h-4 w-4" />;
    case "signature_block":
      return <FileSignature className="h-4 w-4" />;
    case "static_content":
    case "custom":
      return <PenSquare className="h-4 w-4" />;
    default:
      return <Type className="h-4 w-4" />;
  }
}

function elementAccentClass(type: TemplatePageElementType) {
  switch (type) {
    case "text":
      return "from-slate-500/10 via-zinc-500/6 to-transparent";
    case "rich_text":
      return "from-violet-500/10 via-fuchsia-500/6 to-transparent";
    case "image":
    case "logo":
      return "from-sky-500/10 via-cyan-500/6 to-transparent";
    case "service_table":
    case "pricing_summary":
      return "from-emerald-500/10 via-teal-500/6 to-transparent";
    case "signature_block":
      return "from-blue-500/10 via-indigo-500/6 to-transparent";
    case "badge":
      return "from-amber-500/10 via-orange-500/6 to-transparent";
    default:
      return "from-white/10 via-white/6 to-transparent";
  }
}

function FieldLabel({
  label,
  hint,
}: {
  label: string;
  hint?: string;
}) {
  return (
    <div className="mb-2">
      <label className="block text-xs font-medium uppercase tracking-[0.14em] text-white/35">
        {label}
      </label>
      {hint ? <p className="mt-1 text-xs text-white/35">{hint}</p> : null}
    </div>
  );
}

function EditorSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="border-b border-white/10 px-4 py-3">
        <p className="text-sm font-medium text-white/88">{title}</p>
        {description ? (
          <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
        ) : null}
      </div>
      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function ToggleField({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-black/20 text-white"
      />
      <div>
        <p className="text-sm font-medium text-white/86">{label}</p>
        <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
      </div>
    </label>
  );
}

function Input({
  value,
  onChange,
  disabled,
  placeholder,
  type = "text",
}: {
  value: string | number;
  onChange: (value: string) => void;
  disabled: boolean;
  placeholder?: string;
  type?: "text" | "number";
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
    />
  );
}

function TextArea({
  value,
  onChange,
  disabled,
  placeholder,
  rows,
  mono = false,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  placeholder?: string;
  rows: number;
  mono?: boolean;
}) {
  return (
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className={cn(
        "w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40",
        mono && "font-mono"
      )}
    />
  );
}

function Select({
  value,
  onChange,
  disabled,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  children: ReactNode;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
    >
      {children}
    </select>
  );
}

export function BuilderElementContentEditor({
  element,
  readOnly = false,
  onChange,
  className,
}: BuilderElementContentEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadImage, isPending: isUploading, error: uploadError } =
    useBuilderImageUpload();

  const isDataElement =
    element.type === "company_info" ||
    element.type === "project_summary" ||
    element.type === "service_table" ||
    element.type === "pricing_summary" ||
    element.type === "signature_block";

  const isMediaElement = element.type === "image" || element.type === "logo";
  const isCustomContentElement =
    element.type === "static_content" || element.type === "custom";

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-white/10 bg-black/20",
        className
      )}
    >
      <div
        className={cn(
          "border-b border-white/10 bg-gradient-to-r px-4 py-3",
          elementAccentClass(element.type)
        )}
      >
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-2 text-white/65">
            {elementTypeIcon(element.type)}
          </div>

          <div className="min-w-0">
            <p className="text-sm font-medium text-white/88">
              {elementTypeLabel(element.type)} Content
            </p>
            <p className="mt-1 text-xs leading-5 text-white/40">
              Edit the selected block here while using the page stage as the visual reference.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {element.type === "text" && (
          <EditorSection
            title="Text block"
            description="Main body copy for this block."
          >
            <div>
              <FieldLabel
                label="Text"
                hint="This copy renders directly on the page stage."
              />
              <TextArea
                rows={6}
                value={getStringContent(element, "text")}
                onChange={(value) =>
                  onChange(updateElementContent(element, "text", value))
                }
                disabled={readOnly}
                placeholder="Add text for this block..."
              />
            </div>
          </EditorSection>
        )}

        {element.type === "rich_text" && (
          <>
            <EditorSection
              title="Rich text content"
              description="Use HTML when this block needs more structure."
            >
              <div>
                <FieldLabel
                  label="HTML"
                  hint="Headings, paragraphs, lists, and emphasis can all be defined here."
                />
                <TextArea
                  rows={9}
                  mono
                  value={getStringContent(element, "html")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "html", value))
                  }
                  disabled={readOnly}
                  placeholder="<p>Add rich text here...</p>"
                />
              </div>
            </EditorSection>

            <EditorSection
              title="Heading treatment"
              description="Optional heading shown above the rich text body."
            >
              <ToggleField
                label="Show heading"
                description="Turn this on when the block should lead with a visible heading."
                checked={getBooleanContent(element, "showHeading", false)}
                disabled={readOnly}
                onChange={(checked) =>
                  onChange(updateElementContent(element, "showHeading", checked))
                }
              />

              <div>
                <FieldLabel label="Heading text" />
                <Input
                  value={getStringContent(element, "headingText")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "headingText", value))
                  }
                  disabled={readOnly}
                  placeholder="Optional heading"
                />
              </div>
            </EditorSection>
          </>
        )}

        {isMediaElement && (
          <EditorSection
            title={element.type === "logo" ? "Logo source" : "Image source"}
            description="Control the media that renders on the page stage."
          >
            <div className="space-y-4">
              {/* Upload from computer */}
              <div>
                <FieldLabel label="Upload from computer" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  disabled={readOnly || isUploading}
                  className="sr-only"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    // Reset so the same file can be re-picked after an error
                    e.target.value = "";
                    uploadImage(file, {
                      onSuccess: ({ publicUrl }) => {
                        onChange(updateElementContent(element, "src", publicUrl));
                      },
                    });
                  }}
                />
                <button
                  type="button"
                  disabled={readOnly || isUploading}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm transition disabled:opacity-40",
                    isUploading
                      ? "border-white/10 bg-white/[0.03] text-white/40"
                      : "border-white/10 bg-black/20 text-white/70 hover:border-white/20 hover:bg-white/[0.06] hover:text-white",
                  )}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Choose file
                    </>
                  )}
                </button>
                {uploadError && (
                  <p className="mt-1.5 text-xs text-red-400">{uploadError.message}</p>
                )}
              </div>

              {/* Image preview */}
              {getStringContent(element, "src") && (
                <div className="overflow-hidden rounded-xl border border-white/10">
                  <img
                    src={getStringContent(element, "src")}
                    alt={getStringContent(element, "alt") || "preview"}
                    className="max-h-40 w-full object-contain bg-black/20 p-2"
                  />
                </div>
              )}

              <div>
                <FieldLabel
                  label="Image URL"
                  hint="Or paste a source URL directly."
                />
                <Input
                  value={getStringContent(element, "src")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "src", value))
                  }
                  disabled={readOnly}
                  placeholder="https://..."
                />
              </div>

              <div>
                <FieldLabel
                  label="Alt text"
                  hint="Describe the image for accessibility."
                />
                <Input
                  value={getStringContent(element, "alt")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "alt", value))
                  }
                  disabled={readOnly}
                  placeholder="Describe the image"
                />
              </div>

              <div>
                <FieldLabel
                  label="Caption"
                  hint="Optional supporting caption shown below the image."
                />
                <Input
                  value={getStringContent(element, "caption")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "caption", value))
                  }
                  disabled={readOnly}
                  placeholder="Optional caption"
                />
              </div>
            </div>
          </EditorSection>
        )}

        {element.type === "badge" && (
          <EditorSection
            title="Badge content"
            description="Short message and tone for this badge."
          >
            <div className="space-y-4">
              <div>
                <FieldLabel label="Badge text" />
                <Input
                  value={getStringContent(element, "text")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "text", value))
                  }
                  disabled={readOnly}
                  placeholder="Trusted Partner"
                />
              </div>

              <div>
                <FieldLabel label="Tone" />
                <Select
                  value={getStringContent(element, "tone", "default")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "tone", value))
                  }
                  disabled={readOnly}
                >
                  <option value="default" className="bg-[#111]">
                    Default
                  </option>
                  <option value="success" className="bg-[#111]">
                    Success
                  </option>
                  <option value="warning" className="bg-[#111]">
                    Warning
                  </option>
                  <option value="info" className="bg-[#111]">
                    Info
                  </option>
                </Select>
              </div>
            </div>
          </EditorSection>
        )}

        {element.type === "divider" && (
          <EditorSection
            title="Divider settings"
            description="Adjust the visual break between content areas."
          >
            <div className="space-y-4">
              <div>
                <FieldLabel label="Divider style" />
                <Select
                  value={getStringContent(element, "style", "solid")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "style", value))
                  }
                  disabled={readOnly}
                >
                  <option value="solid" className="bg-[#111]">
                    Solid
                  </option>
                  <option value="dashed" className="bg-[#111]">
                    Dashed
                  </option>
                  <option value="subtle" className="bg-[#111]">
                    Subtle
                  </option>
                </Select>
              </div>

              <div>
                <FieldLabel label="Label" hint="Optional small label above the divider." />
                <Input
                  value={getStringContent(element, "label")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "label", value))
                  }
                  disabled={readOnly}
                  placeholder="Optional divider label"
                />
              </div>
            </div>
          </EditorSection>
        )}

        {element.type === "spacer" && (
          <EditorSection
            title="Spacer settings"
            description="Control vertical breathing room in the section."
          >
            <div className="space-y-4">
              <div>
                <FieldLabel label="Height" hint="Use values like 24, 40, or 64." />
                <Input
                  type="number"
                  value={getNumberContent(element, "height", 24)}
                  onChange={(value) =>
                    onChange(
                      updateElementContent(
                        element,
                        "height",
                        Number.isFinite(Number(value)) ? Number(value) : 24
                      )
                    )
                  }
                  disabled={readOnly}
                />
              </div>

              <div>
                <FieldLabel label="Internal label" />
                <Input
                  value={getStringContent(element, "label")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "label", value))
                  }
                  disabled={readOnly}
                  placeholder="Optional internal label"
                />
              </div>
            </div>
          </EditorSection>
        )}

        {isDataElement && (
          <>
            <EditorSection
              title="Heading visibility"
              description="Choose whether this data block shows a heading on the page."
            >
              <ToggleField
                label="Show heading"
                description="Display a visible heading for this data-driven block."
                checked={getBooleanContent(element, "showHeading", true)}
                disabled={readOnly}
                onChange={(checked) =>
                  onChange(updateElementContent(element, "showHeading", checked))
                }
              />
            </EditorSection>

            {(element.type === "service_table" || element.type === "pricing_summary" || element.type === "signature_block") && (
              <EditorSection
                title="Accent color"
                description="Color used for table headers, totals, and signature lines. Defaults to your brand primary color."
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      disabled={readOnly}
                      value={getStringContent(element, "accentColor") || "#374151"}
                      onChange={(e) =>
                        onChange(updateElementContent(element, "accentColor", e.target.value))
                      }
                      className="h-9 w-14 cursor-pointer rounded-lg border border-white/15 bg-transparent p-0.5 disabled:opacity-40"
                    />
                    <Input
                      value={getStringContent(element, "accentColor")}
                      onChange={(value) =>
                        onChange(updateElementContent(element, "accentColor", value))
                      }
                      disabled={readOnly}
                      placeholder="Uses brand color"
                    />
                    {getStringContent(element, "accentColor") && (
                      <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => onChange(updateElementContent(element, "accentColor", ""))}
                        className="shrink-0 rounded-lg border border-white/10 px-2.5 py-2 text-xs text-white/50 hover:text-white/80 disabled:opacity-40"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] leading-4 text-white/35">
                    Leave blank to use your brand primary color from Brand Setup.
                  </p>
                </div>
              </EditorSection>
            )}
          </>
        )}

        {isCustomContentElement && (
          <>
            <EditorSection
              title="Custom content"
              description="Freeform copy for reusable or custom content blocks."
            >
              <div>
                <FieldLabel
                  label="Content"
                  hint="This is the text that renders directly inside the block."
                />
                <TextArea
                  rows={7}
                  value={getStringContent(element, "text")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "text", value))
                  }
                  disabled={readOnly}
                  placeholder="Add custom content here..."
                />
              </div>
            </EditorSection>

            <EditorSection
              title="Internal mapping"
              description="Optional internal key for future template or data mapping."
            >
              <div>
                <FieldLabel label="Content key" />
                <Input
                  value={getStringContent(element, "contentKey")}
                  onChange={(value) =>
                    onChange(updateElementContent(element, "contentKey", value))
                  }
                  disabled={readOnly}
                  placeholder="optional-content-key"
                />
              </div>
            </EditorSection>
          </>
        )}
      </div>
    </div>
  );
}